<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ $t('rescan.title') }}</h3>
          <button @click="$emit('close')" class="btn btn-icon">
            <LineIcon name="x" :size="15" :stroke-width="1.8" />
          </button>
        </div>

        <div class="modal-body">
          <p class="modal-description">
            {{ $t('rescan.description') }}
          </p>

        <!-- Error Message -->
        <div v-if="error" class="error-banner">
          <LineIcon name="alert-circle" :size="15" :stroke-width="1.8" />
          {{ error }}
        </div>

        <div class="modal-options">
          <!-- Normal Scan Option -->
          <button
            @click="$emit('rescan', false)"
            class="modal-option-btn normal-scan"
            :disabled="loading"
          >
            <span class="option-icon">
              <LineIcon name="file-check" :size="18" :stroke-width="1.8" />
            </span>
            <span class="option-text">
              <h4>{{ $t('rescan.normalScan') }}</h4>
              <p>{{ $t('rescan.normalScanDesc') }}</p>
            </span>
          </button>

          <!-- Hard Scan Option -->
          <button
            @click="$emit('rescan', true)"
            class="modal-option-btn hard-scan"
            :disabled="loading || !canUseHardScan"
            :class="{ 'disabled': !canUseHardScan }"
          >
            <span class="option-icon">
              <LineIcon name="layers" :size="18" :stroke-width="1.8" />
            </span>
            <span class="option-text">
              <h4>
                {{ $t('rescan.hardScan') }}
                <span v-if="canUseHardScan" class="premium-badge">PREMIUM</span>
              </h4>
              <p>{{ $t('rescan.hardScanDesc', { remaining: hardScansRemaining, limit: hardScanLimit }) }}</p>
              <p v-if="!canUseHardScan" class="no-scans-warning">{{ $t('rescan.noHardScans') }}</p>
            </span>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <p>{{ $t('rescan.rescanning') }}</p>
        </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps({
  show: {
    type: Boolean,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  canUseHardScan: {
    type: Boolean,
    default: false
  },
  hardScansRemaining: {
    type: Number,
    default: 0
  },
  hardScanLimit: {
    type: Number,
    default: 0
  }
});

defineEmits(['close', 'rescan']);
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
  position: relative;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.modal-description {
  margin: 0 0 24px 0;
  color: var(--text-secondary);
  font-size: 15px;
  text-align: center;
}

.error-banner {
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #dc2626;
  font-size: 14px;
}

.error-banner svg {
  flex-shrink: 0;
}

.modal-options {
  display: grid;
  gap: 12px;
}

.modal-option-btn {
  width: 100%;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-option-btn:not(:disabled):hover {
  border-color: var(--primary);
  background: var(--primary-light);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
}

.modal-option-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-option-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  color: var(--primary);
}

.modal-option-btn h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.premium-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.modal-option-btn p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.no-scans-warning {
  color: #dc2626 !important;
  font-weight: 500;
  font-size: 13px !important;
}

.modal-option-btn.normal-scan:not(:disabled):hover {
  border-color: #10b981;
  background: #ecfdf5;
}

.modal-option-btn.normal-scan:not(:disabled):hover h4 {
  color: #10b981;
}

.modal-option-btn.normal-scan:not(:disabled):hover .option-icon {
  color: #10b981;
}

.modal-option-btn.hard-scan:not(:disabled):hover {
  border-color: #f59e0b;
  background: #fffbeb;
}

.modal-option-btn.hard-scan:not(:disabled):hover h4 {
  color: #f59e0b;
}

.modal-option-btn.hard-scan:not(:disabled):hover .option-icon {
  color: #f59e0b;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-overlay p {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: var(--text-primary);
}

@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-option-btn {
    padding: 16px;
  }
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* The two choices as rows, like the dashboard's and the PDF chooser's: the
   mark, what it does, and what that means. They were centred posters with a
   48px glyph over a 16px heading. */
.modal-content {
  max-width: 400px;
}

.modal-description {
  margin: 0 0 8px;
  font-size: 11px;
  color: #64748b;
  text-align: left;
  font-weight: 400;
}

.modal-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-option-btn {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.modal-option-btn:not(:disabled):hover {
  border-color: #0697e4;
  background: #fff;
  transform: none;
  box-shadow: none;
}

.modal-option-btn.normal-scan:not(:disabled):hover,
.modal-option-btn.hard-scan:not(:disabled):hover {
  border-color: #0697e4;
  background: #fff;
}

.modal-option-btn.normal-scan:not(:disabled):hover h4,
.modal-option-btn.hard-scan:not(:disabled):hover h4,
.modal-option-btn.normal-scan:not(:disabled):hover .option-icon,
.modal-option-btn.hard-scan:not(:disabled):hover .option-icon {
  color: #0697e4;
}

.option-icon {
  display: inline-flex;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  color: #0697e4;
  font-size: inherit;
}

.option-text {
  display: block;
  min-width: 0;
}

.modal-option-btn h4 {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}

.modal-option-btn p {
  margin: 1px 0 0;
  font-size: 11px;
  line-height: 1.4;
  color: #64748b;
}

.premium-badge {
  margin-left: 6px;
  padding: 1px 5px;
  font-size: 9px;
  letter-spacing: .4px;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}
</style>
